import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { BaseButton } from 'components/BaseButton';
import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: any) => ({
  bar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    // Keep actions clear of the home indicator when the dialog is full screen.
    paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': { minHeight: theme.spacing(5.5) },
    },
  },
  inline: {
    padding: theme.spacing(2, 0, 0),
  },
  left: {
    display: 'flex',
    minWidth: 0,
  },
  right: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
}));

type ModalActionBarProps = {
  onCancel: () => void;
  submitText: string;
  submitType?: 'submit' | 'button';
  onSubmit?: () => void;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  cancelText?: string;
  cancelIcon?: IconProp;
  submitIcon?: IconProp;
  leftAction?: ReactNode;
  inline?: boolean;
  form?: string;
};

// DESIGN.md §5 action bar: [left action] <space> [Cancel] [Save], Save rightmost.
const ModalActionBar: FC<ModalActionBarProps> = ({
  onCancel,
  submitText,
  submitType = 'submit',
  onSubmit,
  isSubmitting = false,
  submitDisabled = false,
  cancelText,
  cancelIcon = 'times',
  submitIcon = 'check',
  leftAction,
  inline = false,
  form,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={inline ? `${classes.bar} ${classes.inline}` : classes.bar}>
      <Box className={classes.left}>{leftAction}</Box>
      <Box className={classes.right}>
        <BaseButton
          buttonVariant="outlined"
          buttonColor="inherit"
          icon={cancelIcon}
          text={cancelText || t('common:cancel')}
          disabled={isSubmitting}
          onClick={onCancel}
        />
        <BaseButton
          buttonVariant="contained"
          buttonColor="secondary"
          icon={submitIcon}
          type={submitType}
          form={form}
          text={submitText}
          isLoading={isSubmitting}
          disabled={submitDisabled || isSubmitting}
          onClick={onSubmit ? () => onSubmit() : undefined}
        />
      </Box>
    </Box>
  );
};

export default ModalActionBar;
